import React, { useState, useEffect, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter
} from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  TrendingUp,
  LineChart,
  Table,
  History,
  Loader2,
  Search,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import PriceTrendChart from './PriceTrendChart';
import PriceHistoryTable from './PriceHistoryTable';
import productService from '../services/productService';

const PriceHistoryDrawer = ({
  open,
  onClose,
  // Mode control
  initialMode = 'single', // 'single' | 'advanced'
  // Single mode props
  productId = '',
  productName = '',
  currentQuotationId = null,
  // Advanced mode props (pre-fill search form)
  initialTipDiameter = '',
  initialTotalLength = '',
  initialCategory = '',
  initialClientName = '',
  initialProductName = '',
  // Shared props
  showActions = false,
  onApplyPrice,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Mode state - can toggle between single and advanced
  const [mode, setMode] = useState(initialMode);

  // Component state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [priceHistory, setPriceHistory] = useState([]);
  const [currentTab, setCurrentTab] = useState('table');
  const [searchExpanded, setSearchExpanded] = useState(mode === 'advanced');

  // Pagination state for infinite scroll
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;

  // Fetched product details from API (includes tip_diameter, total_length, etc.)
  const [fetchedProductDetails, setFetchedProductDetails] = useState(null);
  const [productDetailsLoading, setProductDetailsLoading] = useState(false);

  // Search form state (for advanced mode)
  const [searchForm, setSearchForm] = useState({
    tipDiameterMin: '',
    tipDiameterMax: '',
    totalLengthMin: '',
    totalLengthMax: '',
    category: initialCategory,
    clientName: initialClientName,
    productName: initialProductName
  });

  // Statistics
  const [stats, setStats] = useState({
    totalRecords: 0,
    avgPrice: 0,
    minPrice: 0,
    maxPrice: 0,
    quotationCount: 0,
    salesOrderCount: 0,
    uniqueClients: 0,
    uniqueProducts: 0,
  });

  // Reset mode when drawer opens
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setSearchExpanded(initialMode === 'advanced');
      setError('');
      setFetchedProductDetails(null); // Reset fetched product details
      setCurrentPage(1); // Reset pagination
      setHasMore(false);
      setPriceHistory([]); // Clear previous results
    }
  }, [open, initialMode]);

  // Update search form when switching to advanced mode - use fetched product details
  useEffect(() => {
    if (open && mode === 'advanced') {
      // Helper to convert value to string, handling 0 and other numeric values
      const toStringValue = (val) => {
        if (val === undefined || val === null) return '';
        if (typeof val === 'number') return String(val);
        return String(val);
      };

      // Use fetched product details if available, otherwise fall back to initial props
      const tipDiameter = fetchedProductDetails?.tip_diameter ?? initialTipDiameter;
      const totalLength = fetchedProductDetails?.total_length ?? initialTotalLength;
      const category = fetchedProductDetails?.category || initialCategory;
      const productNameValue = fetchedProductDetails?.name || initialProductName;

      const tipDiameterStr = toStringValue(tipDiameter);
      const totalLengthStr = toStringValue(totalLength);

      setSearchForm({
        // Pre-fill from fetched product details
        tipDiameterMin: tipDiameterStr,
        tipDiameterMax: tipDiameterStr,
        totalLengthMin: totalLengthStr,
        totalLengthMax: totalLengthStr,
        category: category || '',
        clientName: initialClientName || '',
        productName: productNameValue || ''
      });
    }
  }, [open, mode, fetchedProductDetails, initialTipDiameter, initialTotalLength, initialCategory, initialClientName, initialProductName]);

  // Build search criteria from form (for advanced mode)
  const buildSearchCriteria = useCallback(() => {
    const criteria = {
      page: 1,
      pageSize: 100,
      sortBy: 'date',
      sortOrder: 'desc'
    };

    // Tip diameter range
    if (searchForm.tipDiameterMin) criteria.tipDiameterMin = parseFloat(searchForm.tipDiameterMin);
    if (searchForm.tipDiameterMax) criteria.tipDiameterMax = parseFloat(searchForm.tipDiameterMax);

    // Total length range
    if (searchForm.totalLengthMin) criteria.totalLengthMin = parseFloat(searchForm.totalLengthMin);
    if (searchForm.totalLengthMax) criteria.totalLengthMax = parseFloat(searchForm.totalLengthMax);

    // Text filters
    if (searchForm.category) criteria.category = searchForm.category;
    if (searchForm.clientName) criteria.clientName = searchForm.clientName;
    if (searchForm.productName) criteria.productName = searchForm.productName;

    return criteria;
  }, [searchForm]);

  // Process and normalize price history data
  const processHistoryData = (historyArray) => {
    return historyArray.map((item, index) => ({
      ...item,
      price: parseFloat(item.price || item.unit_price || item.base_price || 0),
      quantity: parseInt(item.quantity || item.qty || 1),
      date: item.created_at || item.date || item.timestamp || new Date().toISOString(),
      client_name: item.client_name || item.customer_name || item.client || item.customer || '未知客户',
      source_type: item.source_type || item.type || 'quotation',
      reference_id: item.quotation_id || item.sales_order_id || item.id || `${Date.now()}-${index}`,
      base_price: parseFloat(item.base_price || item.price || item.unit_price || 0),
      pricing_mode: item.pricing_mode || 'tax_exclusive',
      tax_rate: parseFloat(item.tax_rate || 0.13),
      tax_exclusive_price: parseFloat(item.tax_exclusive_price || item.price || item.unit_price || 0),
      tax_inclusive_price: parseFloat(item.tax_inclusive_price || (item.price || item.unit_price || 0) * 1.13),
      tax_amount: parseFloat(item.tax_amount || (item.price || item.unit_price || 0) * 0.13),
      formatted_tax_rate: item.formatted_tax_rate || '13.0%',
      product_id: item.product_id || '',
      product_name: item.product_name || item.name || '',
      specification: item.specification || item.spec || ''
    })).filter(item => item.price > 0);
  };

  // Calculate statistics from data
  const calculateStats = (processedHistory) => {
    if (processedHistory.length === 0) {
      return {
        totalRecords: 0, avgPrice: 0, minPrice: 0, maxPrice: 0,
        quotationCount: 0, salesOrderCount: 0, uniqueClients: 0, uniqueProducts: 0
      };
    }

    const prices = processedHistory.map(item => item.price);
    const clients = [...new Set(processedHistory.map(item => item.client_name))];
    const products = [...new Set(processedHistory.map(item => item.product_id).filter(Boolean))];

    return {
      totalRecords: processedHistory.length,
      avgPrice: prices.reduce((sum, price) => sum + price, 0) / prices.length,
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
      quotationCount: processedHistory.filter(item => item.source_type === 'quotation').length,
      salesOrderCount: processedHistory.filter(item => item.source_type === 'sales_order').length,
      uniqueClients: clients.length,
      uniqueProducts: products.length
    };
  };

  // Load price history - single product mode
  const loadSingleProductHistory = useCallback(async (page = 1, append = false) => {
    if (!productId) {
      setError('产品ID不存在');
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setProductDetailsLoading(true);
    }
    setError('');

    try {
      const response = await productService.searchProductPriceHistory(productId, {
        currentQuotationId,
        page,
        pageSize: PAGE_SIZE
      });
      const history = response.price_history || [];
      const historyArray = Array.isArray(history) ? history : [];
      const processedHistory = processHistoryData(historyArray);

      if (append) {
        setPriceHistory(prev => [...prev, ...processedHistory]);
      } else {
        setPriceHistory(processedHistory);
      }

      // Update pagination state
      setCurrentPage(page);
      setHasMore(response.pagination?.has_more || false);

      // Store fetched product details for use in advanced mode (only on first load)
      if (!append && response.product_details) {
        setFetchedProductDetails(response.product_details);
      }

      // Use statistics from response or calculate
      if (response.statistics) {
        setStats({
          totalRecords: response.statistics.total_records || response.total_records || processedHistory.length,
          avgPrice: response.statistics.avg_price || 0,
          minPrice: response.statistics.min_price || 0,
          maxPrice: response.statistics.max_price || 0,
          quotationCount: response.statistics.quotation_count || 0,
          salesOrderCount: response.statistics.sales_order_count || 0,
          uniqueClients: response.statistics.unique_clients || 0,
          uniqueProducts: response.statistics.unique_products || 1,
        });
      } else if (!append) {
        setStats(calculateStats(processedHistory));
      }
    } catch (err) {
      console.error('Price history loading error:', err);
      setError(err.response?.data?.error || err.message || '加载价格历史失败');
    } finally {
      setLoading(false);
      setProductDetailsLoading(false);
      setLoadingMore(false);
    }
  }, [productId, currentQuotationId, PAGE_SIZE]);

  // Load price history - advanced mode
  const loadAdvancedHistory = useCallback(async (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const criteria = buildSearchCriteria();
      criteria.page = page;
      criteria.pageSize = PAGE_SIZE;

      const response = await productService.searchAdvancedPriceHistory(criteria);
      const history = response.price_history || [];
      const historyArray = Array.isArray(history) ? history : [];
      const processedHistory = processHistoryData(historyArray);

      if (append) {
        setPriceHistory(prev => [...prev, ...processedHistory]);
      } else {
        setPriceHistory(processedHistory);
      }

      // Update pagination state
      setCurrentPage(page);
      setHasMore(response.pagination?.has_more || false);

      if (response.statistics) {
        setStats({
          totalRecords: response.statistics.total_records || response.total_records || processedHistory.length,
          avgPrice: response.statistics.avg_price || 0,
          minPrice: response.statistics.min_price || 0,
          maxPrice: response.statistics.max_price || 0,
          quotationCount: response.statistics.quotation_count || 0,
          salesOrderCount: response.statistics.sales_order_count || 0,
          uniqueClients: response.statistics.unique_clients || 0,
          uniqueProducts: response.statistics.unique_products || 0,
        });
      } else if (!append) {
        setStats(calculateStats(processedHistory));
      }

    } catch (err) {
      console.error('Advanced price history search error:', err);
      setError(err.response?.data?.error || err.message || '搜索价格历史失败');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildSearchCriteria, PAGE_SIZE]);

  // Auto-load in single mode when drawer opens
  useEffect(() => {
    if (open && mode === 'single' && productId) {
      loadSingleProductHistory();
    }
  }, [open, mode, productId, loadSingleProductHistory]);

  // Handle search form change
  const handleFormChange = (field, value) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle search button click
  const handleSearch = () => {
    setCurrentPage(1);
    setHasMore(false);
    loadAdvancedHistory(1, false);
  };

  // Load more for infinite scroll
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    if (mode === 'single') {
      loadSingleProductHistory(nextPage, true);
    } else {
      loadAdvancedHistory(nextPage, true);
    }
  }, [loadingMore, hasMore, currentPage, mode, loadSingleProductHistory, loadAdvancedHistory]);

  // Handle clear
  const handleClear = () => {
    setSearchForm({
      tipDiameterMin: '',
      tipDiameterMax: '',
      totalLengthMin: '',
      totalLengthMax: '',
      category: '',
      clientName: '',
      productName: ''
    });
    setPriceHistory([]);
    setStats({
      totalRecords: 0, avgPrice: 0, minPrice: 0, maxPrice: 0,
      quotationCount: 0, salesOrderCount: 0, uniqueClients: 0, uniqueProducts: 0
    });
    setSearchExpanded(true);
    setCurrentPage(1);
    setHasMore(false);
  };

  // Handle price selection
  const handlePriceSelect = (priceData) => {
    if (onApplyPrice && typeof onApplyPrice === 'function') {
      onApplyPrice(priceData);
      onClose();
    }
  };

  // Get title based on mode
  const getTitle = () => {
    if (mode === 'single' && productName) {
      return `${productName} - 价格历史`;
    }
    return '价格历史搜索';
  };

  // Get search summary for advanced mode
  const getSearchSummary = () => {
    if (mode === 'single') {
      return productId ? `产品编号: ${productId}` : '';
    }
    const parts = [];
    if (searchForm.tipDiameter || searchForm.tipDiameterMin || searchForm.tipDiameterMax) {
      parts.push('刀尖直径');
    }
    if (searchForm.totalLength || searchForm.totalLengthMin || searchForm.totalLengthMax) {
      parts.push('总长度');
    }
    if (searchForm.category) parts.push(`分类: ${searchForm.category}`);
    if (searchForm.clientName) parts.push(`客户: ${searchForm.clientName}`);
    if (searchForm.productName) parts.push(`产品: ${searchForm.productName}`);
    return parts.length > 0 ? parts.join(' | ') : '请输入搜索条件';
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-[90vw] max-w-[90vw] p-0 flex flex-col"
      >
        {/* Header */}
        <SheetHeader className="p-4 pb-2">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <div>
              <SheetTitle>{getTitle()}</SheetTitle>
              <SheetDescription>{getSearchSummary()}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Separator />

        {/* Mode Toggle Row */}
        <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b">
          <div className="flex items-center gap-2">
            <Badge variant={mode === 'single' ? 'default' : 'secondary'}>
              {mode === 'single' ? '单品查询' : '高级搜索'}
            </Badge>
            {priceHistory.length > 0 && (
              <Badge variant="outline" className="text-xs">
                {stats.totalRecords} 条记录
              </Badge>
            )}
            {/* Show loading indicator when fetching product details */}
            {productDetailsLoading && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>加载产品信息...</span>
              </div>
            )}
            {/* Show fetched product details */}
            {fetchedProductDetails && !productDetailsLoading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {fetchedProductDetails.tip_diameter && (
                  <span>刃径: {fetchedProductDetails.tip_diameter}mm</span>
                )}
                {fetchedProductDetails.total_length && (
                  <span>总长: {fetchedProductDetails.total_length}mm</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Label
              htmlFor="mode-switch"
              className={`text-sm cursor-pointer ${mode === 'single' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
            >
              单品
            </Label>
            <Switch
              id="mode-switch"
              checked={mode === 'advanced'}
              disabled={productDetailsLoading}
              onCheckedChange={(checked) => {
                const newMode = checked ? 'advanced' : 'single';
                setMode(newMode);
                setSearchExpanded(newMode === 'advanced');
                if (newMode === 'single' && productId) {
                  loadSingleProductHistory();
                }
              }}
            />
            <Label
              htmlFor="mode-switch"
              className={`text-sm cursor-pointer ${mode === 'advanced' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
            >
              高级
            </Label>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Advanced Mode: Search Form */}
          {mode === 'advanced' && (
            <Collapsible open={searchExpanded} onOpenChange={setSearchExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted rounded-none border-b">
                  <span className="text-sm font-medium">搜索条件</span>
                  {searchExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 bg-background border-b">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    {/* Tip Diameter Min */}
                    <div className="space-y-2">
                      <Label>最小刃径 (mm)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.0"
                        value={searchForm.tipDiameterMin}
                        onChange={(e) => handleFormChange('tipDiameterMin', e.target.value)}
                      />
                    </div>

                    {/* Tip Diameter Max */}
                    <div className="space-y-2">
                      <Label>最大刃径 (mm)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="999.0"
                        value={searchForm.tipDiameterMax}
                        onChange={(e) => handleFormChange('tipDiameterMax', e.target.value)}
                      />
                    </div>

                    {/* Total Length Min */}
                    <div className="space-y-2">
                      <Label>最小总长 (mm)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="0.0"
                        value={searchForm.totalLengthMin}
                        onChange={(e) => handleFormChange('totalLengthMin', e.target.value)}
                      />
                    </div>

                    {/* Total Length Max */}
                    <div className="space-y-2">
                      <Label>最大总长 (mm)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="999.0"
                        value={searchForm.totalLengthMax}
                        onChange={(e) => handleFormChange('totalLengthMax', e.target.value)}
                      />
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                      <Label>分类</Label>
                      <Input
                        placeholder="产品分类"
                        value={searchForm.category}
                        onChange={(e) => handleFormChange('category', e.target.value)}
                      />
                    </div>

                    {/* Client Name */}
                    <div className="space-y-2">
                      <Label>客户名称</Label>
                      <Input
                        placeholder="客户名称"
                        value={searchForm.clientName}
                        onChange={(e) => handleFormChange('clientName', e.target.value)}
                      />
                    </div>

                    {/* Product Name */}
                    <div className="space-y-2">
                      <Label>产品名称</Label>
                      <Input
                        placeholder="产品名称"
                        value={searchForm.productName}
                        onChange={(e) => handleFormChange('productName', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Search Buttons */}
                  <div className="flex items-center gap-2 mt-4">
                    <Button onClick={handleSearch} disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                      搜索
                    </Button>
                    <Button variant="outline" onClick={handleClear}>
                      清空条件
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Results Area */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">正在加载价格历史...</p>
              </div>
            ) : error ? (
              <div className="p-6">
                <Alert variant="destructive">
                  <AlertDescription className="flex items-center justify-between">
                    {error}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={mode === 'single' ? loadSingleProductHistory : loadAdvancedHistory}
                    >
                      重试
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            ) : priceHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <History className="h-16 w-16 text-muted-foreground" />
                <h3 className="text-lg font-medium text-muted-foreground">暂无价格历史</h3>
                <p className="text-sm text-muted-foreground">
                  {mode === 'single' ? '该产品暂无历史价格记录' : '请输入搜索条件查询历史价格'}
                </p>
              </div>
            ) : (
              <div className="h-full flex flex-col">
                {/* Statistics Summary */}
                <div className="p-3 bg-muted border-b">
                  <div className="flex flex-wrap gap-2 items-center">
                    <Badge variant="outline" className="gap-1 text-xs">
                      <History className="h-3 w-3" />
                      {stats.totalRecords} 条记录
                    </Badge>
                    <Badge variant="outline" className="text-primary border-primary/30 text-xs">
                      ¥{stats.avgPrice.toFixed(2)} 平均价格
                    </Badge>
                    <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                      {stats.quotationCount} 报价单
                    </Badge>
                    <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                      {stats.salesOrderCount} 销售订单
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {stats.uniqueClients} 个客户
                    </Badge>
                    {mode === 'advanced' && stats.uniqueProducts > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {stats.uniqueProducts} 个产品
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Tabs */}
                <Tabs value={currentTab} onValueChange={setCurrentTab} className="flex-1 flex flex-col">
                  <div className="px-4 border-b">
                    <TabsList className="h-10">
                      <TabsTrigger value="table" className="gap-1 text-sm">
                        <Table className="h-3 w-3" />
                        {isMobile ? '明细' : '详细记录'}
                      </TabsTrigger>
                      <TabsTrigger value="chart" className="gap-1 text-sm">
                        <LineChart className="h-3 w-3" />
                        {isMobile ? '趋势' : '价格趋势'}
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  {/* Tab Content */}
                  <div className="flex-1 overflow-auto p-4">
                    <TabsContent value="table" className="mt-0">
                      <PriceHistoryTable
                        data={priceHistory}
                        onApplyPrice={handlePriceSelect}
                        showActions={showActions}
                        sortable={true}
                        showProductIdColumn={mode === 'advanced'}
                        // Infinite scroll props
                        infiniteScroll={true}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                        onLoadMore={handleLoadMore}
                        totalRecords={stats.totalRecords}
                      />
                    </TabsContent>

                    <TabsContent value="chart" className="mt-0">
                      <div>
                        <PriceTrendChart
                          data={priceHistory}
                          height={isMobile ? 250 : 350}
                          showTrendLine={true}
                          showAverage={true}
                          interactive={true}
                        />

                        {priceHistory.length >= 2 && (
                          <div className="mt-6">
                            <h4 className="text-sm font-semibold mb-2">最新价格动态</h4>
                            <div className="flex flex-wrap gap-2">
                              {priceHistory
                                .slice()
                                .sort((a, b) => new Date(b.date) - new Date(a.date))
                                .slice(0, 3)
                                .map((item, index) => (
                                  <Badge
                                    key={index}
                                    variant="outline"
                                    className={item.source_type === 'quotation' ? 'border-blue-300 text-blue-600' : 'border-green-300 text-green-600'}
                                  >
                                    {item.client_name}: ¥{item.price.toFixed(2)}
                                  </Badge>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <Separator />
        <SheetFooter className="p-4">
          <Button variant="outline" onClick={onClose}>
            关闭
          </Button>
          {priceHistory.length > 0 && (
            <Button
              onClick={mode === 'single' ? loadSingleProductHistory : loadAdvancedHistory}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <TrendingUp className="h-4 w-4 mr-2" />
              )}
              刷新数据
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default PriceHistoryDrawer;
